import { sanitizeUrl } from '@braintree/sanitize-url';
import React, { useEffect } from 'react';
import { useInRouterContext, useNavigate } from 'react-router';
import isNode from './is-node';

const BLOCKED_URL = 'about:blank';

// react-router 7.18+ rejects any target that isn't a same-origin path from
// `navigate()`, throwing "External navigation is not allowed". Only in-app
// paths are routed; absolute URLs, protocol-relative `//host`, and non-http
// schemes get a full page load instead.
function isInAppPath(url: string) {
  return url.startsWith('/') && !url.startsWith('//');
}

/**
 * RRE marshals this RoutingResult into the Component.RoutingResult proto message.
 * If you make changes here, make sure RRE is updated accordingly.
 * Eventually we may want to share this type between commons and RRE but it's small and unlikely to change much.
 */
export interface RoutingResult {
  status?: RoutingStatus;
  redirect_url?: string;
}

export enum RoutingStatus {
  /* eslint-disable no-shadow */
  Ok = 'OK',
  Redirect = 'REDIRECT',
  NotFound = 'NOT_FOUND',
  UnknownError = 'UNKNOWN_ERROR',
  /* eslint-enable no-shadow */
}

/**
 * Allow child components to signal that the result of rendering should affect routing.
 * This gets extracted by RRE for setting HTTP status codes and headers. In the future it may be used
 * for client-side route transitions as well.
 *
 * Inspired by React Router 5's use of context: https://reacttraining.com/react-router/web/guides/server-rendering
 */
export const RoutingResultContext = React.createContext<RoutingResult>({ status: RoutingStatus.Ok });

/**
 * Sets a 404 status code on the response when rendered server-side. You should probably
 * use components/not_found.tsx to render the 404 page instead of rendering this component directly.
 */
export function NotFoundStatusCode() {
  return (
    <RoutingResultContext.Consumer>
      {result => {
        // eslint-disable-next-line no-param-reassign
        result.status = RoutingStatus.NotFound;
        return null;
      }}
    </RoutingResultContext.Consumer>
  );
}

/**
 * Sets a redirect status code and location on the response when rendered server-side
 *
 * @param {string} props.url - Redirect destination
 * @param {boolean} props.fullPageReload - Forces a full page client-side reload instead of using
 *    react-router. Offsite and non-http destinations do a full page load regardless.
 */
export function Redirect(props: { url: string, fullPageReload?: boolean }) {
  const safeUrl = sanitizeUrl(props.url) === BLOCKED_URL ? BLOCKED_URL : props.url;

  const inRouterContext = useInRouterContext();
  // useNavigate blows up if there's no <RouterProvider> above it, which
  // happens when the header renders outside the router tree. A
  // given <Redirect> is either always inside a router or never, depending on
  // where it's mounted, so this conditional never flips between renders and
  // is safe even though the hooks lint rule can't tell that statically.
  // CoreLink does the same thing.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouterContext ? useNavigate() : undefined;

  // Routing through react-router needs all three: the caller hasn't asked for
  // a full reload, there's an ancestor router to navigate within, and the
  // destination is a path react-router will accept.
  const shouldUseRouter = !props.fullPageReload && !!navigate && isInAppPath(safeUrl);

  // react-router warns (and may misbehave under concurrent rendering) if
  // `navigate()` is called synchronously during render, so it's deferred to
  // an effect. `window.location.assign` isn't a React API and has no such
  // restriction, so it can still run directly during render below.
  useEffect(() => {
    if (isNode || !shouldUseRouter || !navigate) {
      return;
    }

    navigate(safeUrl, { replace: true });
  }, [safeUrl, navigate, shouldUseRouter]);

  if (!isNode) {
    if (!shouldUseRouter) {
      window.location.assign(safeUrl);
    }

    return null;
  }

  return (
    <RoutingResultContext.Consumer>
      {result => {
        /* eslint-disable no-param-reassign */
        result.status = RoutingStatus.Redirect;
        result.redirect_url = safeUrl;
        /* eslint-enable no-param-reassign */

        return (
          <div>
            You are being <a href={safeUrl}>redirected</a>
          </div>
        );
      }}
    </RoutingResultContext.Consumer>
  );
}
